/**
 * When a data source fetches by itself.
 *
 * Every one of the plan's networked sources — the METAR/TAF briefing, the winds
 * aloft, the terrain profile, the openAIP feed — answers the same four questions,
 * and answering them in four different components is how an app ends up with a
 * wall of buttons. So the schedule lives here, once, as a plain object with no
 * React and no DOM in it:
 *
 *   · **the input changed** → fetch, after one debounce for the whole gesture
 *     (dragging a waypoint across the map is a dozen commits, and only where it
 *     is dropped is worth a request)
 *   · **the answer ages** → fetch again, either on a fixed beat (`everyMs`, a
 *     METAR cycle) or when the pilot comes back to a tab whose answer is older
 *     than `staleMs`
 *   · **the tab is hidden** → fetch nothing at all, and re-examine the schedule
 *     on the way back, so a plan left open overnight does not spend the night
 *     polling — and a tab switched away and back inside the beat costs nothing
 *   · **the call failed** → say so and try again, 30 s then every 2 min, until
 *     it works or the input changes. That interval belongs to the backoff: no
 *     focus and no tab switch shortens it, or one dead endpoint would be asked
 *     once per gesture
 *
 * Two rules the callers depend on. A fetch that is already out is ABORTED when
 * its input changes — the answer would describe yesterday's route — and a
 * signature that has not moved never starts a second identical call, which is
 * what keeps a twelve-leg route at one request per source.
 *
 * The signature is the whole input, as one comparable string: `''` means "there
 * is nothing to ask about" and cancels everything without erasing what the plan
 * already knows.
 */

/** Why a run started — passed to `run` so a source can tell a sweep from an edit. */
export type LoadReason = 'change' | 'refresh' | 'stale' | 'timer' | 'retry' | 'resume';

export interface AutoState {
  busy: boolean;
  /** the last failure, still unresolved; cleared by the next success */
  error: unknown;
  /** epoch ms of the last SUCCESSFUL run in this session, or null */
  at: number | null;
  /** consecutive failures — drives the backoff */
  fails: number;
}

const IDLE: AutoState = { busy: false, error: null, at: null, fails: 0 };

/** 30 s, then every 2 min. Short enough to catch a dropped connection, long
 *  enough that a dead endpoint is not hammered while the pilot fills the form. */
export const RETRY_MS: readonly number[] = [30_000, 120_000];

export interface AutoLoadOptions {
  /** the fetch itself; it must reject on failure and honour `signal` */
  run(ctx: { signal: AbortSignal; reason: LoadReason }): Promise<void>;
  /** one debounce for a whole gesture */
  debounceMs: number;
  /** re-run on this beat while the tab is visible; 0 / absent = never */
  everyMs?: number;
  /** re-run when the tab comes back and the answer is older than this */
  staleMs?: number;
  retryMs?: readonly number[];
  /** injectable clock — the tests run on fake timers */
  now?(): number;
}

export class AutoLoader {
  private readonly opts: AutoLoadOptions;
  private readonly retry: readonly number[];
  private readonly clock: () => number;

  /** what the plan currently asks; '' = nothing to ask */
  private sig = '';
  /** the signature the last successful run answered */
  private done = '';
  private state: AutoState = IDLE;
  private subs = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private ctl: AbortController | null = null;
  /** only the newest run may write state — an aborted one must not */
  private token = 0;
  /** between `start()` and `stop()`; outside it nothing is ever scheduled */
  private live = false;
  private visible = true;
  /** something was due while the tab was hidden */
  private waiting: LoadReason | null = null;
  /**
   * Epoch ms before which nothing may be asked again, because the last call
   * FAILED and the backoff owns the interval. 0 = no outage in progress.
   *
   * It exists because a failure leaves `done !== sig`, which on its own reads as
   * "never answered, ask now" — and every window focus and every tab switch calls
   * {@link poke}. Without this the 30 s / 2 min backoff was bypassed by ordinary
   * interaction and a dead endpoint was asked once per gesture, for every source
   * at once.
   */
  private retryAt = 0;

  constructor(opts: AutoLoadOptions) {
    this.opts = opts;
    this.retry = opts.retryMs ?? RETRY_MS;
    this.clock = opts.now ?? (() => Date.now());
  }

  /* ---------------- reading ---------------- */

  subscribe = (cb: () => void): (() => void) => {
    this.subs.add(cb);
    return () => this.subs.delete(cb);
  };

  /** A stable snapshot: the same object until something really moved. */
  getState = (): AutoState => this.state;

  /* ---------------- lifecycle ---------------- */

  /**
   * Begin scheduling. Called from the mounting effect and safe to call again
   * after {@link stop} — React's StrictMode mounts every effect twice, and a
   * loader that could not be revived would leave the app with no data at all.
   */
  start(): void {
    if (this.live) return;
    this.live = true;
    const due = this.dueReason();
    if (due) this.arm(due === 'change' ? this.opts.debounceMs : 0, due);
    // a second mount must not lose the beat, or the outage's backoff, that the
    // first one's `stop()` cleared
    else this.rearm();
  }

  /** Stop scheduling and abort whatever is out. The state is kept. */
  stop(): void {
    this.live = false;
    this.clear();
    this.abort();
    this.patch({ busy: false });
  }

  /* ---------------- input ---------------- */

  /**
   * The input the plan now asks about. Identical signatures are ignored — that
   * is the rule that keeps a keystroke somewhere else in the form free.
   */
  update(sig: string): void {
    if (sig === this.sig) return;
    this.sig = sig;
    this.waiting = null;
    // a new question is not the failed one being repeated: the backoff is over
    this.retryAt = 0;
    // whatever is out was asked about the OLD input
    this.abort();
    this.clear();
    if (sig === '') {
      this.patch({ busy: false, error: null });
      return;
    }
    if (this.live) this.arm(this.opts.debounceMs, 'change');
  }

  /** The one ⟳: fetch now, whatever the schedule says — including a backoff. */
  refresh(): void {
    if (this.sig === '') return;
    this.clear();
    this.retryAt = 0;
    this.patch({ fails: 0 });
    this.begin('refresh');
  }

  /** The tab became visible / hidden (`document.visibilitychange`). */
  setVisible(visible: boolean): void {
    if (visible === this.visible) return;
    this.visible = visible;
    if (!visible) {
      // a run that was armed for later is remembered, not lost — but only as
      // "look again on the way back", never as "run on the way back": a tab
      // switch is not a reason to fetch, and on a source with a beat there is
      // always a timer armed (see `poke`)
      if (this.timer !== null) this.waiting = this.waiting ?? 'resume';
      this.clear();
      return;
    }
    this.poke();
  }

  /**
   * Re-examine the schedule — the window regained focus, say. Runs at once when
   * something is due (a stale answer, a missed beat, an input never answered),
   * and otherwise puts back the timer the hidden tab took away.
   *
   * `waiting` says only that the schedule has to be re-examined; whether anything
   * is DUE is always re-derived from the clock here. Anything else made a tab
   * switch cost a request: a source with a beat always has a timer armed, so
   * "there was a timer" would be permanently true.
   */
  poke(): void {
    if (!this.live || !this.visible) return;
    const waited = this.waiting !== null;
    this.waiting = null;
    const due = this.dueReason();
    if (!due) {
      // the beat (or a retry) was cleared when the tab went away — arm what is left of it
      if (waited || this.timer === null) this.rearm();
      return;
    }
    this.clear();
    this.begin(due === 'change' ? 'resume' : due);
  }

  /* ---------------- the schedule ---------------- */

  /** Why a run is due right now, or null when none is. */
  private dueReason(): LoadReason | null {
    if (this.sig === '') return null;
    if (this.state.busy) return null;
    // an outage owns the interval until its retry falls due; `update()` and
    // `refresh()` are the two things that end it early
    if (this.retryAt > 0 && this.clock() < this.retryAt) return null;
    if (this.done !== this.sig) return 'change';
    const { at } = this.state;
    if (at === null) return 'change';
    const age = this.clock() - at;
    const stale = this.opts.staleMs ?? 0;
    if (stale > 0 && age >= stale) return 'stale';
    const every = this.opts.everyMs ?? 0;
    if (every > 0 && age >= every) return 'timer';
    return null;
  }

  /**
   * Put back the timer a hidden tab cleared: what is left of the backoff during
   * an outage, otherwise what is left of the beat. Nothing to arm on a source
   * with neither — a stale answer is caught by {@link poke} itself.
   */
  private rearm(): void {
    if (!this.live || !this.visible || this.sig === '' || this.state.busy) return;
    if (this.timer !== null) return;
    if (this.retryAt > 0) {
      this.arm(Math.max(0, this.retryAt - this.clock()), 'retry');
      return;
    }
    const every = this.opts.everyMs ?? 0;
    const { at } = this.state;
    if (every > 0 && at !== null) this.arm(Math.max(0, every - (this.clock() - at)), 'timer');
  }

  private arm(ms: number, reason: LoadReason): void {
    this.clear();
    if (!this.live) return;
    if (!this.visible) {
      this.waiting = reason;
      return;
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      this.begin(reason);
    }, Math.max(0, ms));
  }

  private clear(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private abort(): void {
    this.ctl?.abort();
    this.ctl = null;
    this.token++;
  }

  private begin(reason: LoadReason): void {
    if (this.sig === '' || !this.live) return;
    if (!this.visible) {
      this.waiting = reason;
      return;
    }
    this.abort();
    const ctl = new AbortController();
    const mine = ++this.token;
    const asked = this.sig;
    this.ctl = ctl;
    this.patch({ busy: true, error: null });
    void this.opts
      .run({ signal: ctl.signal, reason })
      .then(() => {
        if (mine !== this.token) return;
        this.ctl = null;
        this.done = asked;
        this.retryAt = 0;
        this.patch({ busy: false, error: null, at: this.clock(), fails: 0 });
        const every = this.opts.everyMs ?? 0;
        if (every > 0) this.arm(every, 'timer');
      })
      .catch((e: unknown) => {
        if (mine !== this.token) return;
        this.ctl = null;
        const fails = this.state.fails + 1;
        const wait = this.retry[Math.min(fails - 1, this.retry.length - 1)] ?? 0;
        // the interval belongs to the backoff, not to the next gesture
        this.retryAt = this.clock() + wait;
        this.patch({ busy: false, error: e, fails });
        this.arm(wait, 'retry');
      });
  }

  private patch(next: Partial<AutoState>): void {
    const merged: AutoState = { ...this.state, ...next };
    if (
      merged.busy === this.state.busy &&
      merged.error === this.state.error &&
      merged.at === this.state.at &&
      merged.fails === this.state.fails
    ) {
      return;
    }
    this.state = merged;
    for (const cb of this.subs) cb();
  }
}

/* ---------------- how old the answer is ---------------- */

/** A minute, in ms — the boundary between "gerade eben" and "vor n min". */
const MIN_MS = 60_000;
/** An hour: past it the age is printed as the clock time it was read at. */
export const REL_LIMIT_MS = 60 * MIN_MS;

export type Age =
  | { kind: 'none' }
  /** less than a minute ago */
  | { kind: 'now' }
  /** whole minutes, 1..59 */
  | { kind: 'min'; mins: number }
  /** an hour or more: show the time it was read at instead */
  | { kind: 'time' };

/**
 * How to say when this was read. A pilot reads "vor 4 min" faster than a clock
 * time, and a clock time faster than "vor 143 min" — so the two swap over at an
 * hour, which is also where a METAR stops being current.
 */
export function ageOf(at: number | null, now: number): Age {
  if (at === null || !Number.isFinite(at)) return { kind: 'none' };
  const d = now - at;
  if (d < 0) return { kind: 'now' };
  if (d < MIN_MS) return { kind: 'now' };
  if (d < REL_LIMIT_MS) return { kind: 'min', mins: Math.floor(d / MIN_MS) };
  return { kind: 'time' };
}

/** How old is too old, per source. `null` for data that does not go off. */
export interface AgeLimits {
  /** past this the answer is worth a second look */
  warn: number;
  /** past this it should not be planned on */
  bad: number;
}

/**
 * Whether an answer's age is worth marking.
 *
 * Colour is never the only carrier — the age itself is always printed beside it
 * (the same rule the flight-category dot follows), so this only adds emphasis to
 * a number the pilot can already read.
 */
export function ageLevel(at: number | null, now: number, limits: AgeLimits | null): '' | 'warn' | 'bad' {
  if (at === null || !Number.isFinite(at) || !limits) return '';
  const d = now - at;
  if (d >= limits.bad) return 'bad';
  if (d >= limits.warn) return 'warn';
  return '';
}
